import { createAsyncThunk } from '@reduxjs/toolkit';
import { ResponseErrorWithHandled } from '../../shared/types';
import {
  getLinkedInIntegrations,
  connectLinkedInIntegration,
  rotateApiKey,
  updateFieldMappings,
  deleteLinkedInIntegration,
  verifyLinkedInIntegration,
  reconnectLinkedInIntegration,
  getLinkedInCampaigns,
  getLinkedInAccounts,
  getStepConfig,
  retryLinkedInTasks,
  getFailedLinkedInTasks,
  getLinkedInAutomationTourSeen,
  dismissLinkedInAutomationTour,
} from './helpers/linkedin-automation.api';
import { LinkedInPlatform } from './enums';
import {
  LinkedInIntegration,
  LinkedInCampaign,
  LinkedInAccount,
  LinkedInStepConfig,
  FailedTaskDetail,
  VerifyIntegrationResponse,
} from './types';

export const getLinkedInIntegrationsRequest = createAsyncThunk<
  LinkedInIntegration[],
  void,
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/getLinkedInIntegrationsRequest', async (_args, thunkAPI) => {
  try {
    return await getLinkedInIntegrations();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const connectIntegrationRequest = createAsyncThunk<
  LinkedInIntegration,
  { platform: LinkedInPlatform; apiKey: string },
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/connectIntegrationRequest', async (payload, thunkAPI) => {
  try {
    return await connectLinkedInIntegration(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const rotateApiKeyRequest = createAsyncThunk<
  LinkedInIntegration,
  { id: number; apiKey: string },
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/rotateApiKeyRequest', async ({ id, apiKey }, thunkAPI) => {
  try {
    return await rotateApiKey(id, { apiKey });
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const updateFieldMappingsRequest = createAsyncThunk<
  LinkedInIntegration,
  { id: number; fieldMappings: Array<{ platformField: string; shFieldId: number | null }> },
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/updateFieldMappingsRequest', async ({ id, fieldMappings }, thunkAPI) => {
  try {
    return await updateFieldMappings(id, { fieldMappings });
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const deleteIntegrationRequest = createAsyncThunk<
  void,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/deleteIntegrationRequest', async (id, thunkAPI) => {
  try {
    return await deleteLinkedInIntegration(id);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const verifyIntegrationRequest = createAsyncThunk<
  VerifyIntegrationResponse,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/verifyIntegrationRequest', async (id, thunkAPI) => {
  try {
    return await verifyLinkedInIntegration(id);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const reconnectIntegrationRequest = createAsyncThunk<
  LinkedInIntegration,
  { id: number; apiKey?: string },
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/reconnectIntegrationRequest', async ({ id, apiKey }, thunkAPI) => {
  try {
    return await reconnectLinkedInIntegration(id, apiKey);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getCampaignsRequest = createAsyncThunk<
  { campaigns: LinkedInCampaign[]; cachedAt: string },
  { integrationId: number; refresh?: boolean },
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/getCampaignsRequest', async ({ integrationId, refresh }, thunkAPI) => {
  try {
    return await getLinkedInCampaigns(integrationId, refresh);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getLinkedInAccountsRequest = createAsyncThunk<
  LinkedInAccount[],
  number,
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/getLinkedInAccountsRequest', async (integrationId, thunkAPI) => {
  try {
    return await getLinkedInAccounts(integrationId);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getStepConfigRequest = createAsyncThunk<
  LinkedInStepConfig,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/getStepConfigRequest', async (stepId, thunkAPI) => {
  try {
    return await getStepConfig(stepId);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const retryLinkedInTasksRequest = createAsyncThunk<
  { retried: number[]; skipped: number[] },
  { taskIds: number[]; fieldUpdates?: Record<string, string> },
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/retryLinkedInTasksRequest', async ({ taskIds, fieldUpdates }, thunkAPI) => {
  try {
    return await retryLinkedInTasks(taskIds, fieldUpdates);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getFailedLinkedInTasksRequest = createAsyncThunk<
  { tasks: FailedTaskDetail[]; total: number },
  { page?: number; limit?: number },
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/getFailedLinkedInTasksRequest', async (filters, thunkAPI) => {
  try {
    return await getFailedLinkedInTasks(filters);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getLinkedInTourSeenRequest = createAsyncThunk<
  { seen: boolean },
  void,
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/getLinkedInTourSeenRequest', async (_args, thunkAPI) => {
  try {
    return await getLinkedInAutomationTourSeen();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const dismissLinkedInTourRequest = createAsyncThunk<
  { success: boolean },
  void,
  { rejectValue: ResponseErrorWithHandled }
>('linkedInAutomation/dismissLinkedInTourRequest', async (_args, thunkAPI) => {
  try {
    return await dismissLinkedInAutomationTour();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});
